import React, { useEffect, useState, ForwardRefRenderFunction } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { IMarker } from "@/widgets/savePosition/libs/types/IMarker";
import { getVideoState } from "@/entities/video/model/selectors/getVideoState";
import { IAppVideoData } from "@/features/auth/types/auth";
import { modalMenuPhoneActions } from "@/entities/modalMenuPhone";
import { videoActions } from "@/entities/video/model/slice/videoSlice";
import { savePositionActions } from "@/entities/savePosition";
import styles from "./PhotoCardList.module.scss";
import { useGetUserQuery } from "@/features/auth/api/authAPI";
import { RightClickModal } from "@/shared/ui/RightClickModal/RightClickModal";
import { useSelectedScreens } from "@/entities/selectedScreens";
import { SIMILAR_SCREEN_STORAGE_KEY } from "@/Views/SimilarScreen/SimilarScreenModal/SimilarScreenModal";
import { useNavigate } from "react-router-dom";

interface PhotoCardListProps {
  onSeek: (time: number) => void;
  appVideoScreens: any[]; // Replace 'any' with the correct type
  setImagesReady: (state: boolean) => void;
  imagesReady: boolean;
  apiDataVideosOne?: IAppVideoData;
  setIsActivePoster: (state: boolean) => void;
  isModalOpenPhotoCard: boolean;
  handleOpenBillingPage: () => void;
  refetchDataScreens: () => void;
}

const PhotoCardList: ForwardRefRenderFunction<
  HTMLDivElement,
  PhotoCardListProps
> = (
  {
    appVideoScreens,
    setImagesReady,
    onSeek,
    apiDataVideosOne,
    isModalOpenPhotoCard,
    handleOpenBillingPage,
    refetchDataScreens,
  },
  ref,
) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentPlayTime } = useSelector(getVideoState);

  const [selectedImage, setSelectedImage] = useState("");
  const [imageIndex, setImageIndex] = useState(0);
  const [hoveredTimestamp, setHoveredTimestamp] = useState<string | null>(null);
  const [selectedTimestamp, setSelectedTimestamp] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState();
  const [loadedImagesCount, setLoadedImagesCount] = useState(0);
  const token = localStorage.getItem("access_token");

  const { data: userData } = useGetUserQuery(null, { skip: !token });
  const [isHover, setIsHover] = useState(false);

  const isFirstImage = imageIndex === 0;
  const isLastImage = imageIndex === appVideoScreens.length - 1;

  // Add selected screens functionality
  const {
    addScreen,
    removeScreen,
    isSelected: checkIsSelected,
    selectedScreens,
  } = useSelectedScreens();
  const [modalImageSelected, setModalImageSelected] = useState(false);

  const totalImages = appVideoScreens.length ? appVideoScreens.length - 1 : 100;

  const images = appVideoScreens.map((item) => item.screen) ?? [];

  const itemScreen = [...appVideoScreens]
    .sort((a, b) => Number(a.timestamp) - Number(b.timestamp))
    .map((item) => {
      return item;
    });

  const handlePrevImage = () => {
    const prevIndex = (imageIndex - 1 + images.length) % images.length;
    const prevImage = itemScreen[prevIndex];
    setSelectedImage(prevImage.screen);
    setSelectedTimestamp(prevImage.timestamp);
    setSelectedItem(prevImage);
    setImageIndex(prevIndex);
  };

  const handleNextImage = () => {
    const nextIndex = (imageIndex + 1) % images.length;
    const nextImage = itemScreen[nextIndex];
    setSelectedImage(nextImage.screen);
    setSelectedTimestamp(nextImage.timestamp);
    setSelectedItem(nextImage);
    setImageIndex(nextIndex);
  };

  const openModal = (item, image: string, timestamp: string) => {
    const index = images.indexOf(image);
    setSelectedImage(image);
    setSelectedTimestamp(timestamp);
    setSelectedItem(item);
    setImageIndex(index);
    dispatch(modalMenuPhoneActions.showModalOpenPhotoCard());
    const newMarker: IMarker = {
      timestamp: timestamp.toString(),
      appVideo: apiDataVideosOne!.id,
    };

    // setMarkers((prevMarkers) => [...prevMarkers, newMarker]);
    dispatch(savePositionActions.addMarker(newMarker));
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight") {
      handleNextImage();
    } else if (event.key === "ArrowLeft") {
      handlePrevImage();
    }
  };

  const closeModal = () => {
    dispatch(modalMenuPhoneActions.hideModalOpenPhotoCard());
    dispatch(savePositionActions.resetMarker());
  };

  const handleClickScreen = (item, itemScreens: string, timestamp: string) => {
    openModal(item, itemScreens, timestamp);
  };

  useEffect(() => {
    if (isModalOpenPhotoCard) {
      window.addEventListener("keydown", handleKeyDown);
    } else {
      window.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpenPhotoCard, handleKeyDown]);

  const handleImageLoad = () => {
    setLoadedImagesCount((count) => count + 1);
  };

  useEffect(() => {
    if (loadedImagesCount >= totalImages) {
      setImagesReady(true);
    } else {
      setImagesReady(false);
    }
  }, [loadedImagesCount, totalImages, setImagesReady]);

  const handleImageClick = () => {
    dispatch(videoActions.setPlaying(false));
    onSeek(selectedTimestamp ? Number(selectedTimestamp) + 0.01 : 0);
    closeModal();
    if (ref && "current" in ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "instant" });
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 500);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 500);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    imageId: string;
    timestamp: string;
  } | null>(null);

  const handleContextMenu = (
    event: React.MouseEvent,
    imageId: string,
    timestamp: string,
  ) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      imageId,
      timestamp,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Handle selection for the main image view
  const handleSelectImage = (e: React.MouseEvent, item) => {
    e.stopPropagation();

    if (checkIsSelected(item.id)) {
      removeScreen(item.id);
      // If the modal is open and showing this image, update modal selection state too
      if (selectedItem && selectedItem.id === item.id) {
        setModalImageSelected(false);
      }
    } else {
      addScreen({
        id: item.id,
        ts: item.timestamp,
        app_video: apiDataVideosOne?.id,
        isSaved: item.is_saved,
        is_saved_pk: item.is_saved_pk,
      });
      // If the modal is open and showing this image, update modal selection state too
      if (selectedItem && selectedItem.id === item.id) {
        setModalImageSelected(true);
      }
    }
  };

  // Handle selection in the modal
  const handleModalSelectImage = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!selectedItem) return;

    if (checkIsSelected(selectedItem.id)) {
      removeScreen(selectedItem.id);
      setModalImageSelected(false);
    } else {
      addScreen({
        id: selectedItem.id,
        ts: selectedItem.timestamp,
        app_video: apiDataVideosOne?.id,
        isSaved: selectedItem.is_saved,
        is_saved_pk: selectedItem.is_saved_pk,
      });
      setModalImageSelected(true);
    }
  };

  // Update modal image selection state when selectedScreens changes
  useEffect(() => {
    if (selectedItem) {
      setModalImageSelected(checkIsSelected(selectedItem.id));
    }
  }, [selectedScreens, selectedItem, checkIsSelected]);

  const handleSimilarClick = (e: React.MouseEvent, item) => {
    e.stopPropagation();
    e.preventDefault();

    if (!item) return;

    const similarScreenData = {
      id: item.id,
      screen: item.screen,
      appId: apiDataVideosOne?.id,
      timestamp: item.timestamp,
      appIcon: apiDataVideosOne?.app.icon || "",
      appName: apiDataVideosOne?.app.name || "",
      appDescription: apiDataVideosOne?.app.shortname || "",
    };

    localStorage.setItem(
      SIMILAR_SCREEN_STORAGE_KEY,
      JSON.stringify(similarScreenData),
    );

    // Open in new tab
    window.open(`/similar-screens/${item.id}`, "_blank");
  };

  return (
    <div
      className={`${styles.root} ${isModalOpenPhotoCard ? styles.noDisplay : ""}`}
      ref={ref}
    >
      {Object.values(itemScreen).map((item, i) => {
        const highlighted =
          Number(itemScreen[i].timestamp) < currentPlayTime &&
          (Number(itemScreen[i + 1]?.timestamp) || 99999) > currentPlayTime;

        const imgClass = highlighted ? styles.photoContainer : "";
        const isItemSelected = checkIsSelected(item.id);
        const imageAlt = item.vtags ? item.vtags.join(", ") : "";

        return (
          item.screen && (
            <div
              key={i}
              onMouseEnter={() => setHoveredTimestamp(item.timestamp)}
              onMouseLeave={() => setHoveredTimestamp(null)}
              className={`${styles.photoItemContainer} ${item.is_blurry ? styles.featuredItem : ""}`}
            >
              <div
                key={item.id}
                className={`${imgClass} ${styles.photoItem}`}
                onClick={() =>
                  handleClickScreen(item, item.screen, item.timestamp)
                }
                onContextMenu={(e) =>
                  handleContextMenu(e, item.id.toString(), item.timestamp)
                }
              >
                <div
                  className={
                    !userData?.is_pro && item.is_blurry
                      ? styles.screenItemContainerNotLogged
                      : ""
                  }
                >
                  {!userData?.is_pro && item.is_blurry && (
                    <div className={styles.screenItemContentNotLogged}>
                      <div className={styles.titleNotLogged}>
                        <p className={styles.titleNotLoggedPro}>
                          <span className={styles.span}>Pro</span>
                        </p>
                        <p className={styles.titleNotLoggedMember}>
                          Member's Only
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenBillingPage();
                        }}
                        className={styles.buttonNotLogged}
                      >
                        <span className={styles.span}>Unlock Pro</span>
                      </button>
                    </div>
                  )}
                  <div
                    className={
                      !userData?.is_pro && item.is_blurry
                        ? styles.imageContainerNotLogged
                        : ""
                    }
                  >
                    <img
                      className={`${styles.highlighted} ${!userData?.is_pro && item.is_blurry ? styles.opacitymain : ""} ${item.is_blurry ? styles.featuredImage : ""}`}
                      src={item.screen}
                      alt={imageAlt}
                      onLoad={handleImageLoad}
                      loading="lazy"
                    />
                    {(hoveredTimestamp === item.timestamp ||
                      isItemSelected) && (
                      <div className={styles.selectButtonContainer}>
                        <div className={styles.selectButtonContainerInner}>
                          {hoveredTimestamp === item.timestamp &&
                            item.is_saved && (
                              <div className={styles.savedTag}>
                                <p>Saved</p>
                              </div>
                            )}
                          <button
                            className={`${styles.selectButton} ${isItemSelected ? styles.selected : ""}`}
                            onClick={(e) => handleSelectImage(e, item)}
                          >
                            {isItemSelected && (
                              <img
                                src="/img/blackIcon/Check.svg"
                                alt="selectImage"
                              />
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                    {hoveredTimestamp === item.timestamp && !item.is_blurry && !userData?.is_pro && (
                      <div className={styles.bottomMenuContainer}>
                        <div className={styles.bottomMenuContainerInner}>
                          <button onClick={(e) => handleSimilarClick(e, item)}>
                            <img src="/img/similar-icon.svg" alt="similar" />
                            <p>Similar</p>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        );
      })}
      <Modal
        isOpen={isModalOpenPhotoCard}
        onRequestClose={closeModal}
        contentLabel="Image Modal"
        className={styles.modal}
        overlayClassName={styles.overlay}
      >
        <div className={styles.modalContent}>
          <div
            className={styles.containerImage}
            onContextMenu={(e) =>
              handleContextMenu(
                e,
                selectedItem?.id.toString() || "",
                selectedTimestamp,
              )
            }
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
          >
            <img
              className={styles.modalContentPhoto}
              src={selectedImage}
              alt="Enlarged pic"
              loading="lazy"
            />
            {!selectedItem?.is_blurry && (
              <>
                {(isHover || modalImageSelected) && (
                  <div className={styles.modalSelectButtonContainer}>
                    <div className={styles.modalSelectButtonContainerInner}>
                      {isHover && selectedItem && selectedItem.is_saved && (
                        <div className={styles.savedTag}>
                          <p>Saved</p>
                        </div>
                      )}

                      <button
                        className={`${styles.selectButton} ${modalImageSelected ? styles.selected : ""}`}
                        onClick={handleModalSelectImage}
                      >
                        {modalImageSelected && (
                          <img
                            src="/img/blackIcon/Check.svg"
                            alt="selectImage"
                          />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {isHover && (
                  <div className={styles.modalBottomMenuContainer}>
                    <div className={styles.modalBottomMenuContainerInner}>
                      <button onClick={(e) => handleSimilarClick(e, selectedItem)}>
                        <img src="/img/similar-icon.svg" alt="similar" />
                        <p>Similar</p>
                      </button>
                      <button onClick={handleImageClick}>
                        <img src="/img/see-video-icon.svg" alt="see-video" />
                        <p>See Video</p>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <button
              type="button"
              onClick={handleNextImage}
              className={styles.arrowButton}
              style={{
                display: isLastImage ? "none" : "block",
              }}
            >
              <img
                className={styles.arrowRight}
                src="/img/whiteIcon/ChevronRight.svg"
                alt="arrowRightImg"
                loading="lazy"
              />
            </button>
            <button
              type="button"
              onClick={handlePrevImage}
              className={styles.arrowButton}
              style={{
                display: isFirstImage ? "none" : "block",
              }}
            >
              <img
                className={styles.arrowLeft}
                src="/img/whiteIcon/ChevronLeft.svg"
                alt="arrowLeftImg"
                loading="lazy"
              />
            </button>
          </div>
        </div>
      </Modal>
      {contextMenu && (
        <RightClickModal
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={handleCloseContextMenu}
          imageUrl={
            appVideoScreens.find(
              (screen) => screen.id.toString() === contextMenu.imageId,
            )?.screen || ""
          }
          imageId={contextMenu.imageId || ""}
          timestamp={contextMenu.timestamp}
          refetchData={refetchDataScreens}
          avsID={apiDataVideosOne?.id}
          imageType="video"
          onlyCopy
        />
      )}
    </div>
  );
};

export default React.forwardRef(PhotoCardList);
